import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { blogPosts, blogCategories } from "../../../mocks/blog";
import BlogCard from "./BlogCard";
import BlogFilters from "./BlogFilters";

const PAGE_SIZE = 6;

interface BlogGridProps {
  search: string;
  activeCategory: string;
}

export default function BlogGrid({ search, activeCategory }: BlogGridProps) {
  const [page, setPage] = useState(1);

  const sortedPosts = useMemo(
    () => [...blogPosts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    []
  );

  const featured = sortedPosts.find((p) => p.featured);
  const rest = sortedPosts.filter((p) => !p.featured);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { All: sortedPosts.length };
    blogCategories.slice(1).forEach((cat) => {
      counts[cat] = sortedPosts.filter((p) => p.category === cat).length;
    });
    return counts;
  }, [sortedPosts]);

  const filtered = useMemo(() => {
    let pool = activeCategory === "All" ? rest : rest.filter((p) => p.category === activeCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      pool = pool.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q) ||
          p.tags.some((t) => t.toLowerCase().includes(q)) ||
          p.author.name.toLowerCase().includes(q)
      );
      if (
        featured &&
        (featured.title.toLowerCase().includes(q) ||
          featured.excerpt.toLowerCase().includes(q) ||
          featured.tags.some((t) => t.toLowerCase().includes(q)))
      ) {
        return [featured, ...pool];
      }
    }
    return pool;
  }, [activeCategory, rest, search, featured]);

  useEffect(() => {
    setPage(1);
  }, [activeCategory, search]);

  const showFeatured = !search.trim() && activeCategory === "All" && featured;
  const paginated = filtered.slice(0, page * PAGE_SIZE);
  const hasMore = paginated.length < filtered.length;

  return (
    <section className="py-16 bg-[#F8F8F8]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-1.5 h-5 rounded-full bg-[#C9A227]"></div>
              <h2 className="text-xl font-extrabold text-[#222831]">
                {search.trim() ? "Search Results" : activeCategory === "All" ? "All Articles" : activeCategory}
              </h2>
            </div>
            <p className="text-sm text-[#3A506B]/60 pl-4">
              {search.trim()
                ? `${filtered.length} result${filtered.length !== 1 ? "s" : ""} for &ldquo;${search}&rdquo;`
                : `${activeCategory === "All" ? sortedPosts.length : (categoryCounts[activeCategory] ?? 0)} article${
                    (activeCategory === "All" ? sortedPosts.length : (categoryCounts[activeCategory] ?? 0)) !== 1 ? "s" : ""
                  }`}
            </p>
          </div>
          <BlogFilters active={activeCategory} counts={categoryCounts} />
        </div>

        {/* Featured Post */}
        {showFeatured && (
          <div className="mb-10">
            <BlogCard post={featured!} variant="featured" />
          </div>
        )}

        {/* Grid */}
        {paginated.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginated.map((post) => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D9E2EC] mb-4">
              <i className="ri-search-line text-2xl text-[#3A506B]"></i>
            </div>
            <p className="text-base font-semibold text-[#222831]">
              {search.trim()
                ? `No articles match &ldquo;${search}&rdquo;`
                : "No articles in this category yet"}
            </p>
            <p className="text-sm text-[#3A506B]/60 mt-1">
              {search.trim()
                ? "Try a different keyword or browse by category."
                : "Check back soon — we publish weekly."}
            </p>
            <Link
              to="/blog"
              className="mt-5 px-5 py-2.5 rounded-full bg-[#222831] text-white text-sm font-semibold hover:bg-[#3A506B] transition-colors cursor-pointer whitespace-nowrap"
            >
              View All Articles
            </Link>
          </div>
        )}

        {/* Load More */}
        {hasMore && (
          <div className="flex justify-center mt-12">
            <button
              onClick={() => setPage((p) => p + 1)}
              className="px-8 py-3 rounded-full border-2 border-[#222831] text-[#222831] text-sm font-semibold hover:bg-[#222831] hover:text-white transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-refresh-line text-base"></i>
              Load More Articles
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
